import type { components } from "../../../api/generated";
import { feeMoneyCents, feeMoneyText } from "../../fee-adjustment";
import type {
  FinancePayableItinerarySearchResult,
  FinancePayableLine,
  FinancePayablePrepaymentRow,
  FinancePayablePrepaymentSummary,
  FinancePayableSettlementRow,
  FinancePayableSettlementSummary,
  FinancePayableUnassignedGroup,
  FinancePaymentRecordDraft,
  FinancePaymentRequestApprover,
  FinancePaymentRequestAttachment,
  FinancePaymentRequestDetail,
  FinancePaymentRequestDraft,
  FinancePaymentRequestFormContext,
  FinancePaymentRequestLine,
  FinancePaymentRequestPage,
  FinancePaymentRequestPayment,
  FinancePaymentRequestSubmitResult,
  FinancePaymentRequestTarget,
  FinanceOverviewWorkbench,
  FinancePayableOverviewResult,
  FinancePrepaymentItinerary,
  FinancePrepaymentSupplierOption,
  FinanceReceivableOrderLocateItem,
  FinanceReceivableOverviewResult,
  FinanceReceivablePriceTier,
  FinanceReceivableProjectSearchResult,
  FinanceReceivableProjectSearchScope,
  FinanceReceivableReferenceQuote,
  FinanceReceivableSummary,
} from "../types";

export type FinancePayableWorkspaceListRaw = components["schemas"]["CourseRouteWorkspaceListDTO"];

export type FinancePayableSettlementSummaryRaw = components["schemas"]["FinancePayableSettlementSummaryDTO"];

type FinancePayableSettlementRowRaw = components["schemas"]["FinancePayableSettlementRowDTO"];

type FinancePayableLineRaw = components["schemas"]["FinancePayableFulfillmentLineDTO"];

type FinancePayableUnassignedGroupRaw = components["schemas"]["FinancePayableUnassignedSupplierGroupDTO"];

type FinancePayablePrepaymentRowRaw = components["schemas"]["FinancePayablePrepaymentRowDTO"];

export type FinancePayablePrepaymentSummaryRaw = components["schemas"]["FinancePayablePrepaymentSummaryDTO"];

/** 搜团只取第一页，按最近更新排在前面。 */
const FINANCE_PAYABLE_SEARCH_PAGE_SIZE = 20;

/** 映射搜团查询参数：按线路标题关键字查确认件工作区列表。 */
export function mapFinancePayableItinerarySearchParams(keyword: string) {
  return {
    page: 1,
    page_size: FINANCE_PAYABLE_SEARCH_PAGE_SIZE,
    q: keyword,
    sort_field: "updated_at",
    sort_direction: "desc",
  } as const;
}

/** 把确认件工作区列表映射成搜团结果，一份工作区就是一个团。 */
export function mapFinancePayableItinerarySearchResult(
  raw: FinancePayableWorkspaceListRaw,
): FinancePayableItinerarySearchResult {
  return {
    items: raw.items.map((item) => ({
      itineraryId: item.itinerary_id,
      itineraryTitle: item.itinerary_title,
      workspaceStatus: item.status,
      agreementCount: item.order_count,
      unassignedLineCount: item.unassigned_line_count,
    })),
    total: raw.total,
  };
}

/** 映射应付汇总；签约基线为空时保持为空，不补 0。 */
export function mapFinancePayableSettlementSummary(
  raw: FinancePayableSettlementSummaryRaw,
): FinancePayableSettlementSummary {
  return {
    itineraryId: raw.itinerary_id,
    workspace: {
      itineraryTitle: raw.workspace.itinerary_title,
      status: raw.workspace.status,
      serviceDateFrom: raw.workspace.service_date_from ?? null,
      serviceDateTo: raw.workspace.service_date_to ?? null,
    },
    rows: raw.rows.map(mapSettlementRow),
    prepaymentRows: raw.prepayment_rows.map(mapPrepaymentRow),
    unassignedGroups: raw.unassigned.by_settlement_supplier.map(mapUnassignedGroup),
    totals: {
      currentPayableTotal: raw.totals.current_payable_total,
      unassignedTotal: raw.totals.unassigned_total,
      itineraryPayableTotal: raw.totals.itinerary_payable_total,
      requestedTotal: raw.totals.requested_total,
      paidTotal: raw.totals.paid_total,
      unpaidTotal: raw.totals.unpaid_total,
      requestableTotal: raw.totals.requestable_total,
    },
  };
}

/** 映射团还没有确认件时的预付款汇总：团头取行程本身，缺的服务日期保持为空。 */
export function mapFinancePayablePrepaymentSummary(
  raw: FinancePayablePrepaymentSummaryRaw,
): FinancePayablePrepaymentSummary {
  return {
    itineraryId: raw.itinerary_id,
    itineraryTitle: raw.itinerary_title,
    serviceDateFrom: raw.service_date_from ?? null,
    serviceDateTo: raw.service_date_to ?? null,
    prepaymentRows: raw.prepayment_rows.map(mapPrepaymentRow),
    totals: {
      requestedTotal: raw.totals.requested_total,
      paidTotal: raw.totals.paid_total,
      unpaidTotal: raw.totals.unpaid_total,
      requestableTotal: raw.totals.requestable_total,
    },
  };
}

/** 映射预付款那一行。 */
function mapPrepaymentRow(raw: FinancePayablePrepaymentRowRaw): FinancePayablePrepaymentRow {
  return {
    settlementSupplierId: raw.settlement_supplier_id,
    settlementSupplierName: raw.settlement_supplier_name,
    currentPayableAmount: raw.current_payable_amount,
    requestedAmount: raw.requested_amount,
    paidAmount: raw.paid_amount,
    unpaidAmount: raw.unpaid_amount,
    requestableAmount: raw.requestable_amount,
  };
}

/** 映射一行结算主体。 */
function mapSettlementRow(raw: FinancePayableSettlementRowRaw): FinancePayableSettlementRow {
  return {
    agreementId: raw.agreement_id,
    settlementSupplierId: raw.settlement_supplier_id,
    settlementSupplierName: raw.settlement_supplier_name,
    agreementNo: raw.agreement_no,
    agreementStatus: raw.agreement_status,
    currentPayableAmount: raw.current_payable_amount,
    signedBaseline: raw.signed_baseline
      ? {
          baselineAmount: raw.signed_baseline.baseline_amount,
          backfilled: raw.signed_baseline.backfill_status === "backfilled",
        }
      : null,
    changeDeltaAmount: raw.change_delta_amount ?? null,
    changeBatchCount: raw.change_batch_count,
    lines: raw.lines.map(mapLine),
    linesCurrentPayableTotal: raw.lines_current_payable_total,
    unattributedAmount: raw.unattributed_amount,
    requestedAmount: raw.requested_amount,
    paidAmount: raw.paid_amount ?? null,
    unpaidAmount: raw.unpaid_amount ?? null,
    requestableAmount: raw.requestable_amount,
  };
}

/** 映射未归口的一组；结算主体为空的一组保持为空，顺序沿用接口给的。 */
function mapUnassignedGroup(raw: FinancePayableUnassignedGroupRaw): FinancePayableUnassignedGroup {
  return {
    settlementSupplierId: raw.settlement_supplier_id ?? null,
    settlementSupplierName: raw.settlement_supplier_name ?? null,
    lineCount: raw.line_count,
    amountTotal: raw.amount_total,
    lines: raw.lines.map(mapLine),
  };
}

/** 映射一条确认行；可空字段保持为空。 */
function mapLine(raw: FinancePayableLineRaw): FinancePayableLine {
  return {
    lineId: raw.line_id,
    lineNo: raw.line_no,
    resourceType: raw.resource_type,
    itemName: raw.item_name,
    itemSpec: raw.item_spec ?? null,
    serviceProviderName: raw.service_provider_name ?? null,
    serviceProviderType: raw.service_provider_type ?? null,
    quantity: raw.quantity,
    billingCount: raw.billing_count,
    unit: raw.unit,
    specUnit: raw.spec_unit,
    unitPriceAmount: raw.unit_price_amount,
    amount: raw.amount,
    adjustmentAmount: raw.adjustment_amount,
    currentPayableAmount: raw.current_payable_amount,
  };
}

export type FinanceReceivableOrderLocateRaw = components["schemas"]["FinanceReceivableOrderLocateDTO"];

export type FinanceReceivableSummaryRaw = components["schemas"]["FinanceReceivableSummaryDTO"];

type FinanceReceivablePriceTierRaw = components["schemas"]["FinanceReceivablePriceTierDTO"];

type FinanceReceivableReferenceQuoteRaw = components["schemas"]["FinanceReceivableReferenceQuoteDTO"];

export type FinanceReceivableProjectListRaw = components["schemas"]["CustomizationProjectListPage"];

/** 搜项目只取第一页，沿用定制列表默认的按创建时间倒序。 */
const FINANCE_RECEIVABLE_PROJECT_SEARCH_PAGE_SIZE = 20;

/** 映射定位到的订单；团编码、交接单状态为空时保持为空。 */
export function mapFinanceReceivableOrderLocateResult(
  raw: FinanceReceivableOrderLocateRaw,
): FinanceReceivableOrderLocateItem[] {
  return raw.items.map((item) => ({
    orderId: item.order_id,
    teamId: item.team_id ?? null,
    orderStatus: item.order_status ?? null,
  }));
}

/** 映射搜项目的查询参数：定制项目列表按学校、客户、项目名称等关键字搜。 */
export function mapFinanceReceivableProjectSearchParams(keyword: string, scope: FinanceReceivableProjectSearchScope) {
  return { page: 1, page_size: FINANCE_RECEIVABLE_PROJECT_SEARCH_PAGE_SIZE, keyword, scope } as const;
}

/** 把定制项目列表映射成搜项目结果；学校、客户、出发日期没填时保持为空。 */
export function mapFinanceReceivableProjectSearchResult(
  raw: FinanceReceivableProjectListRaw,
): FinanceReceivableProjectSearchResult {
  return {
    items: (raw.items ?? []).map((item) => ({
      projectId: item.id,
      projectName: item.name,
      schoolName: item.organization_name_snapshot ?? null,
      customerName: item.customer_name ?? null,
      departureDate: item.departure_start_date ?? null,
      hasOrder: item.has_order,
    })),
    total: raw.total,
  };
}

/** 映射应收汇总；团编码、名单状态、订单头合同总额与差额为空时保持为空，不补 0。 */
export function mapFinanceReceivableSummary(raw: FinanceReceivableSummaryRaw): FinanceReceivableSummary {
  return {
    orderId: raw.order_id,
    header: {
      teamId: raw.header.team_id ?? null,
      routeName: raw.header.route_name ?? null,
      orderStatus: raw.header.order_status,
      rosterStatus: raw.header.roster_status ?? null,
    },
    rosterFinalized: raw.roster_finalized,
    receivable: {
      frozenBaseReceivableTotal: raw.receivable.frozen_base_receivable_total,
      feeAdjustmentTotal: raw.receivable.fee_adjustment_total,
      currentReceivableTotal: raw.receivable.current_receivable_total,
      orderContractTotal: raw.receivable.order_contract_total ?? null,
      orderContractGap: raw.receivable.order_contract_gap ?? null,
      stoppedMemberCount: raw.receivable.stopped_member_count,
      excludedFeeAdjustmentTotal: raw.receivable.excluded_fee_adjustment_total,
      pendingFeeAdjustmentTotal: raw.receivable.pending_fee_adjustment_total,
    },
    priceTiers: raw.price_tiers.map(mapPriceTier),
    agentCollection: {
      trainTicketAmount: raw.agent_collection.train_ticket_amount,
      flightTicketAmount: raw.agent_collection.flight_ticket_amount,
      intangibleAmount: raw.agent_collection.intangible_amount,
      schoolEnterpriseAmount: raw.agent_collection.school_enterprise_amount,
      ticketAmount: raw.agent_collection.ticket_amount ?? "0.00",
      agentCollectionTotal: raw.agent_collection.agent_collection_total,
      customerPaymentTotal: raw.agent_collection.customer_payment_total,
      excludedItems: raw.agent_collection.excluded_items.map((item) => ({
        category: item.category,
        amount: item.amount,
      })),
    },
    referenceQuote: raw.reference_quote ? mapReferenceQuote(raw.reference_quote) : null,
    received: {
      hasCollection: raw.received.has_collection,
      qrCodeAmount: raw.received.qr_code_amount,
      bankCardAmount: raw.received.bank_card_amount,
      totalAmount: raw.received.total_amount,
    },
    outstanding: {
      kind: raw.outstanding.kind,
      amount: raw.outstanding.amount ?? null,
      overpaidAmount: raw.outstanding.overpaid_amount ?? null,
    },
  };
}

/** 映射参考报价；读不出来或没有时版本与金额保持为空。 */
function mapReferenceQuote(raw: FinanceReceivableReferenceQuoteRaw): FinanceReceivableReferenceQuote {
  return {
    status: raw.status,
    source: raw.source ?? null,
    versionNo: raw.version_no ?? null,
    versionName: raw.version_name ?? null,
    contractReceivableAmount: raw.contract_receivable_amount ?? null,
    agentCollectionAmount: raw.agent_collection_amount ?? null,
  };
}

/** 映射一个价格档；档序与档内成员顺序沿用接口给的。 */
function mapPriceTier(raw: FinanceReceivablePriceTierRaw): FinanceReceivablePriceTier {
  return {
    group: raw.group,
    unitPrice: raw.unit_price,
    memberCount: raw.member_count,
    frozenBaseReceivableSubtotal: raw.frozen_base_receivable_subtotal,
    feeAdjustmentSubtotal: raw.fee_adjustment_subtotal,
    currentReceivableSubtotal: raw.current_receivable_subtotal,
    members: raw.members.map((member) => ({
      memberId: member.member_id,
      memberName: member.member_name,
      frozenBaseReceivableAmount: member.frozen_base_receivable_amount,
      feeAdjustmentAmount: member.fee_adjustment_amount,
      currentReceivableAmount: member.current_receivable_amount,
    })),
  };
}

export type FinancePaymentRequestFormContextRaw = components["schemas"]["PaymentRequestFormContextDTO"];

export type FinancePaymentRequestAttachmentRaw = components["schemas"]["PaymentRequestAttachmentDTO"];

export type FinancePaymentRequestSubmitRaw = components["schemas"]["PaymentRequestSubmitMutation"];

export type FinancePaymentRequestSubmitResultRaw = components["schemas"]["PaymentRequestSubmitResultDTO"];

export type FinancePaymentRequestResubmitRaw = components["schemas"]["PaymentRequestResubmitMutation"];

export type FinancePaymentRequestWithdrawResultRaw = components["schemas"]["PaymentRequestWithdrawResultDTO"];

export type FinancePaymentRequestPageRaw = components["schemas"]["PaymentRequestPageDTO"];

export type FinancePaymentRequestDetailRaw = components["schemas"]["PaymentRequestDetailDTO"];

type FinancePaymentRequestApproverRaw = components["schemas"]["PaymentRequestApproverDTO"];

export type FinancePaymentRequestPaymentRaw = components["schemas"]["PaymentRequestPaymentDTO"];

export type FinancePaymentRecordRaw = components["schemas"]["PaymentRequestPaymentMutation"];

type FinancePaymentRequestLineRaw = components["schemas"]["PaymentRequestLineDTO"];

export type FinancePrepaymentSupplierOptionRaw = components["schemas"]["PaymentRequestSupplierOptionDTO"];

export type FinancePrepaymentItineraryRaw = components["schemas"]["PaymentRequestItineraryDTO"];

/** 映射预付款可选的一个结算主体。 */
export function mapFinancePrepaymentSupplierOption(
  raw: FinancePrepaymentSupplierOptionRaw,
): FinancePrepaymentSupplierOption {
  return { supplierId: raw.supplier_id, supplierName: raw.supplier_name };
}

/** 映射发起预付款时找到的团。 */
export function mapFinancePrepaymentItinerary(raw: FinancePrepaymentItineraryRaw): FinancePrepaymentItinerary {
  return {
    itineraryId: raw.itinerary_id,
    itineraryTitle: raw.itinerary_title,
    serviceDateFrom: raw.service_date_from ?? null,
    serviceDateTo: raw.service_date_to ?? null,
  };
}

/** 映射请款表单要用的数、可选部门、已存收款账户（只有脱敏账号）和可勾的行。 */
export function mapFinancePaymentRequestFormContext(
  raw: FinancePaymentRequestFormContextRaw,
): FinancePaymentRequestFormContext {
  return {
    currentPayableAmount: raw.current_payable_amount,
    requestedAmount: raw.requested_amount,
    availableAmount: raw.available_amount,
    hasAgreement: raw.has_agreement,
    departments: raw.departments.map((item) => ({ deptId: item.dept_id, deptName: item.dept_name, isMain: item.is_main })),
    payeeAccounts: raw.payee_accounts.map((item) => ({
      accountId: item.account_id,
      accountName: item.account_name,
      bankName: item.bank_name,
      bankBranchName: item.bank_branch_name ?? null,
      accountNoMasked: item.account_no_masked,
      isDefault: item.is_default,
    })),
    lines: raw.lines.map(mapPaymentRequestLine),
  };
}

/** 映射一条请款用的确认件行。 */
function mapPaymentRequestLine(raw: FinancePaymentRequestLineRaw): FinancePaymentRequestLine {
  return {
    lineId: raw.line_id,
    lineNo: raw.line_no,
    itemName: raw.item_name,
    itemSpec: raw.item_spec ?? null,
    amount: raw.amount,
  };
}

/** 映射一个请款附件。 */
export function mapFinancePaymentRequestAttachment(
  raw: FinancePaymentRequestAttachmentRaw,
): FinancePaymentRequestAttachment {
  return {
    attachmentId: raw.attachment_id,
    fileName: raw.file_name,
    contentType: raw.content_type,
    fileSize: raw.file_size,
  };
}

/**
 * 把请款草稿映射成提交的请求体：团和结算主体取自应付清单那一行；金额规范成两位小数；
 * 文字去掉两头空白，支行、备注只有空白时传空。选已存收款账户时只传账户 ID，手填的四项传空，
 * 由后端按账户 ID 取原值快照；沿用原收款账户（只在重新提交时有）时账户 ID 和手填四项都传空。
 * 调用前草稿已经过前端校验。
 */
export function mapFinancePaymentRequestMutation(
  target: FinancePaymentRequestTarget,
  draft: FinancePaymentRequestDraft,
): FinancePaymentRequestSubmitRaw {
  const manual = draft.payeeMode === "manual";
  return {
    itinerary_id: target.itineraryId,
    settlement_supplier_id: target.settlementSupplierId,
    payment_type: draft.paymentType as FinancePaymentRequestSubmitRaw["payment_type"],
    amount: feeMoneyText(feeMoneyCents(draft.amount) ?? 0n),
    expected_payment_date: draft.expectedPaymentDate,
    invoice_status: draft.invoiceStatus as FinancePaymentRequestSubmitRaw["invoice_status"],
    payee_account_name: manual ? draft.payeeAccountName.trim() : "",
    payee_bank_name: manual ? draft.payeeBankName.trim() : "",
    payee_bank_branch_name: manual ? draft.payeeBankBranchName.trim() || null : null,
    payee_account_no: manual ? draft.payeeAccountNo.trim() : "",
    purpose: draft.purpose.trim(),
    department_id: draft.departmentId as number,
    attachment_ids: draft.attachments.map((item) => item.attachmentId),
    payee_source_account_id: draft.payeeMode === "saved" ? draft.payeeSourceAccountId : null,
    line_ids: draft.lineIds,
    remark: draft.remark.trim() || null,
  };
}

/** 把修改后重新提交的草稿映射成请求体：同发起请款，另带「沿用原收款账户」的开关。 */
export function mapFinancePaymentRequestResubmitMutation(
  target: FinancePaymentRequestTarget,
  draft: FinancePaymentRequestDraft,
): FinancePaymentRequestResubmitRaw {
  return { ...mapFinancePaymentRequestMutation(target, draft), keep_payee_account: draft.payeeMode === "original" };
}

/** 映射提交结果。 */
export function mapFinancePaymentRequestSubmitResult(
  raw: FinancePaymentRequestSubmitResultRaw,
): FinancePaymentRequestSubmitResult {
  return {
    requestId: raw.request_id,
    requestNo: raw.request_no,
    flowLabel: raw.flow_label,
    stepCount: raw.step_count,
    firstStepApprovers: raw.first_step_approvers.map(mapApprover),
  };
}

/** 映射请款记录的一页。 */
export function mapFinancePaymentRequestPage(raw: FinancePaymentRequestPageRaw): FinancePaymentRequestPage {
  return {
    items: raw.items.map((item) => ({
      requestId: item.request_id,
      requestNo: item.request_no,
      itineraryTitle: item.itinerary_title,
      settlementSupplierName: item.settlement_supplier_name,
      paymentType: item.payment_type,
      amount: item.amount,
      status: item.status,
      currentApprovers: item.current_approvers.map(mapApprover),
      applicantName: item.applicant_name,
      submittedAt: item.submitted_at,
    })),
    total: raw.total,
  };
}

/** 映射请款详情：全部字段、附件、每一轮审批（从新到旧）。 */
export function mapFinancePaymentRequestDetail(raw: FinancePaymentRequestDetailRaw): FinancePaymentRequestDetail {
  return {
    requestId: raw.request_id,
    requestNo: raw.request_no,
    itineraryId: raw.itinerary_id,
    itineraryTitle: raw.itinerary_title,
    serviceDateFrom: raw.service_date_from ?? null,
    serviceDateTo: raw.service_date_to ?? null,
    settlementSupplierId: raw.settlement_supplier_id,
    settlementSupplierName: raw.settlement_supplier_name,
    paymentType: raw.payment_type,
    amount: raw.amount,
    expectedPaymentDate: raw.expected_payment_date,
    invoiceStatus: raw.invoice_status,
    payeeAccountName: raw.payee_account_name,
    payeeBankName: raw.payee_bank_name,
    payeeBankBranchName: raw.payee_bank_branch_name ?? null,
    payeeAccountNoMasked: raw.payee_account_no_masked,
    payeeAccountNo: raw.payee_account_no ?? null,
    purpose: raw.purpose,
    departmentId: raw.department_id,
    departmentName: raw.department_name,
    remark: raw.remark ?? null,
    applicantId: raw.applicant_id,
    applicantName: raw.applicant_name,
    submittedAt: raw.submitted_at,
    status: raw.status,
    currentApprovers: raw.current_approvers.map(mapApprover),
    attachments: raw.attachments.map(mapFinancePaymentRequestAttachment),
    lines: raw.lines.map(mapPaymentRequestLine),
    rounds: raw.rounds.map((round) => ({
      roundNo: round.round_no,
      flowLabel: round.flow_label,
      status: round.status,
      submittedAt: round.submitted_at,
      endedAt: round.ended_at ?? null,
      steps: round.steps.map((step) => ({
        stepNo: step.step_no,
        approverType: step.approver_type,
        approvers: step.approvers.map(mapApprover),
        result: step.result ?? null,
        handledByName: step.handled_by_name ?? null,
        handledAt: step.handled_at ?? null,
        remark: step.remark ?? null,
      })),
    })),
    payments: raw.payments.map(mapFinancePaymentRequestPayment),
  };
}

/** 映射一次已付登记（作废了的也在）及其付款回单。 */
export function mapFinancePaymentRequestPayment(raw: FinancePaymentRequestPaymentRaw): FinancePaymentRequestPayment {
  return {
    paymentId: raw.payment_id,
    payerAccount: raw.payer_account,
    paymentDate: raw.payment_date,
    bankSerialNo: raw.bank_serial_no,
    amount: raw.amount,
    remark: raw.remark ?? null,
    recordedByName: raw.recorded_by_name,
    recordedAt: raw.recorded_at,
    voidedAt: raw.voided_at ?? null,
    voidedByName: raw.voided_by_name ?? null,
    voidReason: raw.void_reason ?? null,
    attachments: raw.attachments.map(mapFinancePaymentRequestAttachment),
  };
}

/** 把已付登记草稿映射成请求体：文字去掉两头空白，备注只有空白时传空；付款金额由后端取请款金额，不传。 */
export function mapFinancePaymentRecordMutation(draft: FinancePaymentRecordDraft): FinancePaymentRecordRaw {
  return {
    payer_account: draft.payerAccount.trim(),
    payment_date: draft.paymentDate,
    bank_serial_no: draft.bankSerialNo.trim(),
    attachment_ids: draft.attachments.map((item) => item.attachmentId),
    remark: draft.remark.trim() || null,
  };
}

/** 映射一位审批人。 */
function mapApprover(raw: FinancePaymentRequestApproverRaw): FinancePaymentRequestApprover {
  return { userId: raw.user_id, name: raw.name };
}

export type FinanceReceivableOverviewPageRaw = components["schemas"]["FinanceOverviewReceivablePageDTO"];

/** 映射应收列表的一页；金额保持两位小数字符串，不经过浮点数。 */
export function mapFinanceReceivableOverviewPage(raw: FinanceReceivableOverviewPageRaw): FinanceReceivableOverviewResult {
  return {
    items: raw.items.map((item) => ({
      orderId: item.order_id,
      teamId: item.team_id,
      orderNo: item.order_no,
      routeName: item.route_name,
      departureDate: item.departure_date,
      customerName: item.customer_name,
      salesUserName: item.sales_user_name,
      rosterFinalized: item.roster_finalized,
      activeMemberCount: item.active_member_count,
      priceTierCount: item.price_tier_count,
      currentReceivableTotal: item.current_receivable_total,
      receivedTotal: item.received_total,
      outstandingKind: item.outstanding_kind,
      outstandingAmount: item.outstanding_amount ?? null,
      overpaidAmount: item.overpaid_amount ?? null,
    })),
    total: raw.total,
    page: raw.page,
    pageSize: raw.page_size,
    totals: {
      teamCount: raw.totals.team_count,
      rosterUnfinalizedCount: raw.totals.roster_unfinalized_count,
      currentReceivableTotal: raw.totals.current_receivable_total,
      receivedTotal: raw.totals.received_total,
      outstandingTotal: raw.totals.outstanding_total,
      legacySystemCount: raw.totals.legacy_system_count,
      quoteUnreadableCount: raw.totals.quote_unreadable_count,
    },
    canViewDetail: raw.can_view_detail,
  };
}

export type FinancePayableOverviewPageRaw = components["schemas"]["FinanceOverviewPayablePageDTO"];

/** 映射应付列表的一页；金额保持两位小数字符串（未付可以为负），不经过浮点数。 */
export function mapFinancePayableOverviewPage(raw: FinancePayableOverviewPageRaw): FinancePayableOverviewResult {
  return {
    items: raw.items.map((item) => ({
      itineraryId: item.itinerary_id,
      orders: item.orders.map((order) => ({ orderId: order.order_id, teamId: order.team_id, orderNo: order.order_no })),
      routeName: item.route_name,
      departureDate: item.departure_date,
      dispatcherNames: item.dispatcher_names,
      settlementSupplierCount: item.settlement_supplier_count,
      itineraryPayableTotal: item.itinerary_payable_total,
      requestedTotal: item.requested_total,
      paidTotal: item.paid_total,
      unpaidTotal: item.unpaid_total,
      requestStatus: item.request_status,
    })),
    total: raw.total,
    page: raw.page,
    pageSize: raw.page_size,
    totals: {
      itineraryCount: raw.totals.itinerary_count,
      itineraryPayableTotal: raw.totals.itinerary_payable_total,
      requestedTotal: raw.totals.requested_total,
      paidTotal: raw.totals.paid_total,
      unpaidTotal: raw.totals.unpaid_total,
    },
    canViewDetail: raw.can_view_detail,
  };
}

export type FinanceOverviewWorkbenchRaw = components["schemas"]["FinanceOverviewWorkbenchDTO"];

/** 映射财务工作台；接口里不出现的项（没有那一页的权限）映射成 null，金额保持两位小数字符串。 */
export function mapFinanceOverviewWorkbench(raw: FinanceOverviewWorkbenchRaw): FinanceOverviewWorkbench {
  const amount = (card: FinanceOverviewWorkbenchRaw["receivable_total"]) =>
    card ? { amount: card.amount, candidateLimitExceeded: card.candidate_limit_exceeded } : null;
  const requestable = raw.requestable_itinerary_count;
  return {
    receivableTotal: amount(raw.receivable_total),
    receivedTotal: amount(raw.received_total),
    itineraryPayableTotal: amount(raw.itinerary_payable_total),
    pendingApprovalCount: raw.pending_approval_count,
    awaitingPaymentCount: raw.awaiting_payment_count ?? null,
    requestableItineraryCount: requestable
      ? { count: requestable.count, candidateLimitExceeded: requestable.candidate_limit_exceeded }
      : null,
  };
}
